import type { TFunction } from "i18next";

export type TurnstileStatus = "idle" | "loading" | "ready" | "load-error" | "widget-error";

export interface FormState {
	status: "idle" | "submitting" | "success" | "error";
	errorMsg: string;
	fieldErrors: Record<string, string>;
	turnstileStatus: TurnstileStatus;
	turnstileToken: string;
	formValid: boolean;
	widgetKey: number;
}

export type FormAction =
	| { type: "TURNSTILE_LOADING" }
	| { type: "TURNSTILE_READY" }
	| { type: "TURNSTILE_LOAD_ERROR" }
	| { type: "TURNSTILE_WIDGET_ERROR" }
	| { type: "SET_TOKEN"; token: string }
	| { type: "SET_VALID"; valid: boolean }
	| { type: "SUBMITTING" }
	| { type: "SUCCESS" }
	| { type: "ERROR"; msg: string }
	| { type: "VALIDATION_ERROR"; errors: Record<string, string> }
	| { type: "RESET" };

export const INITIAL_STATE: FormState = {
	status: "idle",
	errorMsg: "",
	fieldErrors: {},
	turnstileStatus: "idle",
	turnstileToken: "",
	formValid: false,
	widgetKey: 0,
};

const API_ERROR_KEYS: Record<string, string> = {
	"Turnstile not configured": "cta.errorTurnstileNotConfigured",
	"Invalid JSON body": "cta.errorInvalidRequest",
	"Captcha verification failed": "cta.errorCaptcha",
	"This email is already registered": "cta.errorEmailRegistered",
};

export function formReducer(state: FormState, action: FormAction): FormState {
	switch (action.type) {
		case "TURNSTILE_LOADING":
			return { ...state, turnstileStatus: "loading", turnstileToken: "" };
		case "TURNSTILE_READY":
			return { ...state, turnstileStatus: "ready" };
		case "TURNSTILE_LOAD_ERROR":
			return { ...state, turnstileStatus: "load-error", turnstileToken: "" };
		case "TURNSTILE_WIDGET_ERROR":
			return { ...state, turnstileStatus: "widget-error", turnstileToken: "" };
		case "SET_TOKEN":
			return {
				...state,
				turnstileToken: action.token,
				turnstileStatus: action.token ? "ready" : state.turnstileStatus,
			};
		case "SET_VALID":
			return { ...state, formValid: action.valid };
		case "SUBMITTING":
			return { ...state, status: "submitting", fieldErrors: {}, errorMsg: "" };
		case "SUCCESS":
			return { ...state, status: "success" };
		case "ERROR":
			return { ...state, status: "error", errorMsg: action.msg };
		case "VALIDATION_ERROR":
			return { ...state, status: "idle", fieldErrors: action.errors };
		case "RESET":
			return {
				...INITIAL_STATE,
				widgetKey: state.widgetKey + 1,
			};
	}
}

export function mapApiError(error: string | undefined, t: TFunction): string {
	if (!error) return t("cta.errorGeneric");
	const key = API_ERROR_KEYS[error];
	return key ? t(key) : t("cta.errorGeneric");
}

export function getFormData(form: HTMLFormElement): Record<string, unknown> {
	const fd = new FormData(form);
	return {
		name: String(fd.get("name") ?? ""),
		email: String(fd.get("email") ?? ""),
		type: fd.get("type") ?? "person",
		description: String(fd.get("description") ?? ""),
		earlyAccessEmail: fd.get("earlyAccessEmail") === "on",
		updates: fd.get("updates") === "on",
		turnstileToken: "",
	};
}

export function getTurnstileMessage(
	state: FormState,
	sitekey: string | undefined,
	t: TFunction,
): string | null {
	if (!sitekey) return t("cta.turnstileConfigError");
	switch (state.turnstileStatus) {
		case "loading":
			return t("cta.turnstileLoading");
		case "load-error":
			return t("cta.turnstileLoadError");
		case "widget-error":
			return t("cta.turnstileWidgetError");
		case "ready":
			return state.turnstileToken ? null : t("cta.turnstileInstruction");
		default:
			return null;
	}
}

export function isSubmitDisabled(state: FormState): boolean {
	return (
		state.status === "submitting" ||
		!state.formValid ||
		!state.turnstileToken ||
		state.turnstileStatus === "loading" ||
		state.turnstileStatus === "load-error" ||
		state.turnstileStatus === "widget-error"
	);
}
